/**
 * J1 · AudioWorklet pitch tap. Detection runs on the audio thread: the
 * worklet accumulates a rolling 2048-sample window, runs the NSDF hot loop in
 * WASM (bytes emitted by @solfo/shared, instantiated inside the worklet) and
 * posts pitch results stamped with the *audio clock* time of the analyzed
 * window — so a janky main thread can no longer drop frames or skew the
 * trace the way the old AnalyserNode + requestAnimationFrame loop could.
 *
 * The peak-picking half of the detector is not duplicated here: the worklet
 * source embeds `detectPitchFromNsdf` from @solfo/shared via
 * Function.prototype.toString(), so both paths run the exact code the unit
 * tests cover.
 */

import { detectPitchFromNsdf, nsdfWasmBytes } from '@solfo/shared';

export interface WorkletPitchMessage {
  /** AudioContext.currentTime at the END of the analyzed window. */
  time: number;
  midi: number;
  clarity: number;
  rms: number;
}

const WINDOW = 2048;
const HOP = 1024;

function workletSource(): string {
  return `
const detectPitchFromNsdf = ${detectPitchFromNsdf.toString()};

class SolfoPitchProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const o = options.processorOptions;
    this.windowSize = o.windowSize;
    this.hopSize = o.hopSize;
    this.clarityThreshold = o.clarityThreshold;
    this.rmsGate = o.rmsGate;
    this.minLag = Math.max(2, Math.floor(sampleRate / 1500));
    this.maxLag = Math.min(this.windowSize - 1, Math.floor(sampleRate / 55));
    this.ring = new Float32Array(this.windowSize);
    this.filled = 0;
    this.sinceHop = 0;
    this.active = false;
    this.wasm = null;
    this.port.onmessage = (e) => {
      if (e.data === 'start') this.active = true;
      else if (e.data === 'stop') this.active = false;
    };
    WebAssembly.instantiate(o.wasmBytes.buffer).then(({ instance }) => {
      this.wasm = instance.exports;
      const needed = (this.windowSize + this.maxLag + 1) * 4;
      const have = this.wasm.mem.buffer.byteLength;
      if (have < needed) this.wasm.mem.grow(Math.ceil((needed - have) / 65536));
      this.input = new Float32Array(this.wasm.mem.buffer, 0, this.windowSize);
      this.nsdf = new Float32Array(this.wasm.mem.buffer, this.windowSize * 4, this.maxLag + 1);
    });
  }

  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    // slide the ring left by the incoming block, append at the end
    this.ring.copyWithin(0, ch.length);
    this.ring.set(ch, this.windowSize - ch.length);
    this.filled = Math.min(this.windowSize, this.filled + ch.length);
    this.sinceHop += ch.length;

    if (this.active && this.wasm && this.filled >= this.windowSize && this.sinceHop >= this.hopSize) {
      this.sinceHop = 0;
      let sum = 0;
      for (let i = 0; i < this.windowSize; i++) sum += this.ring[i] * this.ring[i];
      const rms = Math.sqrt(sum / this.windowSize);
      if (rms >= this.rmsGate) {
        this.input.set(this.ring);
        this.wasm.nsdf(this.windowSize, this.minLag, this.maxLag);
        const res = detectPitchFromNsdf(this.nsdf, this.minLag, this.maxLag, sampleRate, this.clarityThreshold);
        if (res) {
          this.port.postMessage({ time: currentTime, midi: res.midi, clarity: res.clarity, rms });
        }
      }
    }
    return true;
  }
}

registerProcessor('solfo-pitch', SolfoPitchProcessor);
`;
}

let moduleUrl: string | null = null;
const readyContexts = new WeakSet<BaseAudioContext>();

/** Load the worklet module into the context (once per context). */
export async function ensurePitchWorklet(ac: AudioContext): Promise<boolean> {
  if (!('audioWorklet' in ac)) return false;
  if (readyContexts.has(ac)) return true;
  try {
    if (!moduleUrl) {
      moduleUrl = URL.createObjectURL(new Blob([workletSource()], { type: 'application/javascript' }));
    }
    await ac.audioWorklet.addModule(moduleUrl);
    readyContexts.add(ac);
    return true;
  } catch {
    return false;
  }
}

export function createPitchWorkletNode(
  ac: AudioContext,
  opts: { clarityThreshold: number; rmsGate: number },
): AudioWorkletNode {
  return new AudioWorkletNode(ac, 'solfo-pitch', {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    channelCount: 1,
    processorOptions: {
      wasmBytes: nsdfWasmBytes(),
      windowSize: WINDOW,
      hopSize: HOP,
      clarityThreshold: opts.clarityThreshold,
      rmsGate: opts.rmsGate,
    },
  });
}
